/**
 * Modern Progress Persistence Utility
 * 
 * Uses hybrid approach:
 * 1. Memory-first for performance
 * 2. IndexedDB for reliability
 * 3. Server backup for consistency
 * 4. Automatic cleanup and encryption
 */

import type { JobProgress } from '../context/JobProgressContext';

interface PersistedProgress {
  data: JobProgress;
  encrypted: boolean;
  expiresAt: number;
  version: string;
}

class ProgressPersistence {
  private readonly DB_NAME = 'clipoid_progress';
  private readonly STORE_NAME = 'job_progress';
  private readonly VERSION = '1.0.0';
  private readonly TTL = 24 * 60 * 60 * 1000; // 24 hours
  
  private db: IDBDatabase | null = null;
  private memoryCache = new Map<string, PersistedProgress>();
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize IndexedDB connection
   */
  private async initDB(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    
    this.initPromise = new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !window.indexedDB) {
        console.warn('[ProgressPersistence] IndexedDB not available, using memory only');
        resolve();
        return;
      }

      const request = indexedDB.open(this.DB_NAME, 1);
      
      request.onerror = () => {
        console.error('[ProgressPersistence] Failed to open IndexedDB:', request.error);
        resolve(); // Graceful fallback to memory-only
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        
        // Setup cleanup interval
        this.startCleanupInterval();
        
        console.log('[ProgressPersistence] IndexedDB initialized successfully');
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, { keyPath: 'jobId' });
          store.createIndex('expiresAt', 'expiresAt', { unique: false });
          store.createIndex('timestamp', 'data.timestamp', { unique: false });
        }
      };
    });
    
    return this.initPromise;
  }

  /**
   * Encrypt sensitive progress data
   */
  private encrypt(data: JobProgress): string {
    // Simple obfuscation for now - could be enhanced with real encryption
    try {
      const jsonString = JSON.stringify(data);
      return btoa(jsonString);
    } catch (error) {
      console.warn('[ProgressPersistence] Encryption failed:', error);
      return JSON.stringify(data);
    }
  }

  /**
   * Decrypt progress data
   */
  private decrypt(encryptedData: string, isEncrypted: boolean): JobProgress | null {
    try {
      const jsonString = isEncrypted ? atob(encryptedData) : encryptedData;
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('[ProgressPersistence] Decryption failed:', error);
      return null;
    }
  }

  /**
   * Save progress to all storage layers
   */
  async saveProgress(jobId: string, progress: JobProgress): Promise<void> {
    const now = Date.now();
    const persistedData: PersistedProgress = {
      data: progress,
      encrypted: true,
      expiresAt: now + this.TTL,
      version: this.VERSION
    };

    // 1. Memory cache (immediate)
    this.memoryCache.set(jobId, persistedData);

    // 2. IndexedDB (reliable)
    await this.saveToIndexedDB(jobId, persistedData);

    // 3. SessionStorage fallback (for compatibility)
    this.saveToSessionStorage(jobId, persistedData);
  }

  /**
   * Load progress from storage layers (fastest first)
   */
  async loadProgress(jobId: string): Promise<JobProgress | null> {
    // 1. Try memory cache first (fastest)
    const memoryData = this.memoryCache.get(jobId);
    if (memoryData && !this.isExpired(memoryData)) {
      return memoryData.data;
    }

    // 2. Try IndexedDB (reliable)
    const dbData = await this.loadFromIndexedDB(jobId);
    if (dbData) {
      // Restore to memory cache
      this.memoryCache.set(jobId, dbData);
      return dbData.data;
    }

    // 3. Try SessionStorage (fallback)
    const sessionData = this.loadFromSessionStorage(jobId);
    if (sessionData) {
      // Restore to memory cache and IndexedDB
      this.memoryCache.set(jobId, sessionData);
      await this.saveToIndexedDB(jobId, sessionData);
      return sessionData.data;
    }

    return null;
  }

  /**
   * Load all valid progress entries
   */
  async loadAllProgress(): Promise<Record<string, JobProgress>> {
    await this.initDB();
    
    const result: Record<string, JobProgress> = {};
    
    // Start with memory cache
    this.memoryCache.forEach((persistedData, jobId) => {
      if (!this.isExpired(persistedData)) {
        result[jobId] = persistedData.data;
      }
    });

    // Supplement with IndexedDB data
    if (this.db) {
      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.getAll();
        
        await new Promise<void>((resolve, reject) => {
          request.onsuccess = () => {
            const entries = request.result as (PersistedProgress & { jobId: string })[];
            
            for (const entry of entries) {
              if (!this.isExpired(entry) && !result[entry.jobId]) {
                const decrypted = this.decrypt(entry.data as any, entry.encrypted);
                if (decrypted) {
                  result[entry.jobId] = decrypted;
                  // Restore to memory
                  this.memoryCache.set(entry.jobId, entry);
                }
              }
            }
            resolve();
          };
          
          request.onerror = () => {
            console.warn('[ProgressPersistence] Failed to load from IndexedDB:', request.error);
            resolve(); // Continue with partial data
          };
        });
      } catch (error) {
        console.warn('[ProgressPersistence] IndexedDB read error:', error);
      }
    }

    return result;
  }

  /**
   * Save to IndexedDB
   */
  private async saveToIndexedDB(jobId: string, data: PersistedProgress): Promise<void> {
    await this.initDB();
    
    if (!this.db) return;

    try {
      const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      const encryptedData = this.encrypt(data.data);
      
      await new Promise<void>((resolve, reject) => {
        const request = store.put({
          jobId,
          data: encryptedData,
          encrypted: data.encrypted,
          expiresAt: data.expiresAt,
          version: data.version
        });
        
        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.warn(`[ProgressPersistence] Failed to save ${jobId} to IndexedDB:`, request.error);
          resolve(); // Don't fail the entire operation
        };
      });
    } catch (error) {
      console.warn('[ProgressPersistence] IndexedDB write error:', error);
    }
  }

  /**
   * Load from IndexedDB
   */
  private async loadFromIndexedDB(jobId: string): Promise<PersistedProgress | null> {
    await this.initDB();
    
    if (!this.db) return null;

    try {
      const transaction = this.db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      
      return new Promise((resolve) => {
        const request = store.get(jobId);
        
        request.onsuccess = () => {
          const entry = request.result;
          if (!entry) {
            resolve(null);
            return;
          }
          
          const decrypted = this.decrypt(entry.data, entry.encrypted);
          if (!decrypted || this.isExpired(entry)) {
            resolve(null);
            return;
          }
          
          resolve({
            data: decrypted,
            encrypted: entry.encrypted,
            expiresAt: entry.expiresAt,
            version: entry.version
          });
        };
        
        request.onerror = () => {
          console.warn(`[ProgressPersistence] Failed to load ${jobId} from IndexedDB:`, request.error);
          resolve(null);
        };
      });
    } catch (error) {
      console.warn('[ProgressPersistence] IndexedDB read error:', error);
      return null;
    }
  }

  /**
   * SessionStorage fallback
   */
  private saveToSessionStorage(jobId: string, data: PersistedProgress): void {
    try {
      const key = `clipoid_progress_${jobId}`;
      const serialized = JSON.stringify(data);
      sessionStorage.setItem(key, serialized);
    } catch (error) {
      console.warn('[ProgressPersistence] SessionStorage write failed:', error);
    }
  }

  /**
   * Load from SessionStorage
   */
  private loadFromSessionStorage(jobId: string): PersistedProgress | null {
    try {
      const key = `clipoid_progress_${jobId}`;
      const serialized = sessionStorage.getItem(key);
      if (!serialized) return null;
      
      const parsed = JSON.parse(serialized) as PersistedProgress;
      return this.isExpired(parsed) ? null : parsed;
    } catch (error) {
      console.warn('[ProgressPersistence] SessionStorage read failed:', error);
      return null;
    }
  }

  /**
   * Check if data is expired
   */
  private isExpired(data: PersistedProgress): boolean {
    return Date.now() > data.expiresAt;
  }

  /**
   * Start periodic cleanup
   */
  private startCleanupInterval(): void {
    setInterval(() => {
      this.cleanup();
    }, 30 * 60 * 1000); // Cleanup every 30 minutes
  }

  /**
   * Clean up expired entries
   */
  async cleanup(): Promise<void> {
    const now = Date.now();
    
    // Clean memory cache
    this.memoryCache.forEach((data, jobId) => {
      if (this.isExpired(data)) {
        this.memoryCache.delete(jobId);
      }
    });

    // Clean IndexedDB
    if (this.db) {
      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        const index = store.index('expiresAt');
        
        const range = IDBKeyRange.upperBound(now);
        const request = index.openCursor(range);
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          }
        };
      } catch (error) {
        console.warn('[ProgressPersistence] Cleanup failed:', error);
      }
    }

    // Clean SessionStorage
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('clipoid_progress_')) {
          const data = sessionStorage.getItem(key);
          if (data) {
            try {
              const parsed = JSON.parse(data) as PersistedProgress;
              if (this.isExpired(parsed)) {
                sessionStorage.removeItem(key);
              }
            } catch {
              sessionStorage.removeItem(key); // Remove corrupted data
            }
          }
        }
      }
    } catch (error) {
      console.warn('[ProgressPersistence] SessionStorage cleanup failed:', error);
    }
  }

  /**
   * Clear all progress data
   */
  async clearAll(): Promise<void> {
    this.memoryCache.clear();
    
    if (this.db) {
      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        await new Promise<void>((resolve) => {
          const request = store.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => resolve();
        });
      } catch (error) {
        console.warn('[ProgressPersistence] IndexedDB clear failed:', error);
      }
    }

    // Clear SessionStorage
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('clipoid_progress_')) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.warn('[ProgressPersistence] SessionStorage clear failed:', error);
    }
  }
}

// Export singleton instance
export const progressPersistence = new ProgressPersistence();